import type { ExecutionDashboardProjection } from '../../routes/execution/dashboardProjection';
import type { OperationalActionDescriptor } from '../../routes/execution/executionActionsFacade';
import type { WorkspaceComposition } from './workspaceComposition';
import { buildWorkspaceComposition } from './workspaceComposition';

export type ActionPriority = 'critical' | 'high' | 'medium' | 'low';

export type AttentionItem = {
  id: string;
  priority: ActionPriority;
  icon: string;
  title: string;
  detail: string | null;
  relatedStopUid: string | null;
  relatedIncidentId: number | null;
};

export type WorkflowOrchestration = {
  headline: string | null;
  subline: string | null;
  lifecyclePhase:
    | 'created'
    | 'loading'
    | 'ready_to_dispatch'
    | 'in_transit'
    | 'unloading'
    | 'ready_to_complete'
    | 'completed'
    | 'blocked';
};

export type DispatchPrimaryAction = {
  label: string;
  priority: ActionPriority;
  descriptor: OperationalActionDescriptor | null;
  /** Resolve open incident (blocking workflow). */
  incidentId: number | null;
  opensCompletion: boolean;
};

export type DispatchOrchestration = {
  priority: ActionPriority;
  nextActionLabel: string;
  nextActionDetail: string | null;
  primary: DispatchPrimaryAction;
  secondaryActions: OperationalActionDescriptor[];
  workflow: WorkflowOrchestration;
  attentionItems: AttentionItem[];
  composition: WorkspaceComposition;
};

const PRIMARY_LABELS: Partial<Record<string, string>> = {
  loaded: 'Погрузить',
  in_transit: 'Отправить маршрут',
  unloaded: 'Выгрузить',
  partial_unloaded: 'Частично выгрузить',
  start_loading: 'Погрузить',
  complete_loading: 'Завершить погрузку',
  start_unloading: 'Начать выгрузку',
  complete_unloading: 'Завершить выгрузку',
  complete_route: 'Завершить маршрут',
  report_problem: 'Сообщить о проблеме'
};

const ACTION_RANK: Record<string, number> = {
  loaded: 10,
  start_loading: 10,
  complete_loading: 11,
  in_transit: 20,
  unloaded: 30,
  start_unloading: 30,
  complete_unloading: 31,
  partial_unloaded: 32,
  complete_route: 40,
  report_problem: 90
};

function formatOverdueDuration(plannedAt: string): string {
  const ms = Date.now() - new Date(plannedAt).getTime();
  if (ms <= 0) return '0м';
  const hours = Math.floor(ms / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  if (hours > 0) return `${hours}ч ${mins}м`;
  return `${mins}м`;
}

function actionKey(a: OperationalActionDescriptor): string {
  return `${a.key}:${a.executionStopUid ?? 'route'}`;
}

function displayLabel(descriptor: OperationalActionDescriptor): string {
  return PRIMARY_LABELS[descriptor.key] ?? descriptor.label;
}

function pickWorkflow(dashboard: ExecutionDashboardProjection): WorkflowOrchestration {
  const { routeStatus, progress, vehicleCargo, financeReadiness, activeOperation } = dashboard;
  const allLoadingDone = progress != null && progress.loadingPercent >= 100;
  const inVehicle = vehicleCargo.inVehiclePlaces > 0;
  const hasPendingUnloading = dashboard.routeFlow.stops.some(
    (s) => s.type === 'unloading' && !s.isCompleted
  );
  const allStopsDone =
    progress != null && progress.stopsTotal > 0 && progress.stopsCompleted >= progress.stopsTotal;

  if (routeStatus.hasExecutionFailure || dashboard.incidents.hasActiveBlocking) {
    return {
      headline: 'Требуется вмешательство диспетчера',
      subline: dashboard.incidents.hasActiveBlocking
        ? 'Закройте или снимите блокирующий инцидент'
        : routeStatus.failureReason,
      lifecyclePhase: 'blocked'
    };
  }

  if (financeReadiness.blocked) {
    return {
      headline: 'Ожидаются документы / условия оплаты',
      subline: financeReadiness.blockedReasons.join(' · ') || financeReadiness.detail,
      lifecyclePhase: 'blocked'
    };
  }

  if (routeStatus.transportKey === 'completed' || allStopsDone) {
    if (!inVehicle && (progress?.failedCargoCount ?? 0) === 0) {
      return {
        headline: 'Маршрут готов к завершению',
        subline: 'Подтвердите закрытие маршрута',
        lifecyclePhase: 'ready_to_complete'
      };
    }
    return {
      headline: 'Маршрут завершён',
      subline: null,
      lifecyclePhase: 'completed'
    };
  }

  if (allLoadingDone && routeStatus.transportKey === 'in_transit' && hasPendingUnloading && !inVehicle) {
    return {
      headline: 'Маршрут готов к отправке',
      subline: 'Подтвердите выезд или начните выгрузку на точке',
      lifecyclePhase: 'ready_to_dispatch'
    };
  }

  if (allLoadingDone && !inVehicle && vehicleCargo.deliveredPlaces > 0 && !hasPendingUnloading) {
    return {
      headline: 'Маршрут готов к завершению',
      subline: 'Груз доставлен, машина пуста',
      lifecyclePhase: 'ready_to_complete'
    };
  }

  if (routeStatus.transportKey === 'in_transit' && inVehicle) {
    return {
      headline: 'Маршрут в пути',
      subline: activeOperation
        ? `${activeOperation.operationType === 'unloading' ? 'Выгрузка' : 'Операция'}: ${activeOperation.stopLabel}`
        : `${vehicleCargo.inVehiclePlaces} мест в машине`,
      lifecyclePhase: 'in_transit'
    };
  }

  if (routeStatus.transportKey === 'loading' || routeStatus.transportKey === 'created') {
    return {
      headline: 'Погрузка',
      subline: activeOperation?.address ?? null,
      lifecyclePhase: 'loading'
    };
  }

  if (routeStatus.transportKey === 'unloading') {
    return {
      headline: 'Выгрузка',
      subline: activeOperation?.address ?? null,
      lifecyclePhase: 'unloading'
    };
  }

  return { headline: null, subline: null, lifecyclePhase: 'created' };
}

function buildAttentionItems(
  dashboard: ExecutionDashboardProjection,
  composition: WorkspaceComposition,
  openDocumentTasks: number
): AttentionItem[] {
  const items: AttentionItem[] = [];

  for (const inc of dashboard.incidents.items.filter((i) => i.isActive)) {
    items.push({
      id: `incident-${inc.id}`,
      priority: inc.severity === 'high' ? 'critical' : inc.severity === 'medium' ? 'high' : 'medium',
      icon: inc.severity === 'high' ? '🔴' : '🟠',
      title: inc.typeLabel,
      detail: inc.comment,
      relatedStopUid: inc.relatedExecutionStopUid,
      relatedIncidentId: inc.id
    });
  }

  for (const stop of dashboard.routeFlow.stops) {
    const eta = composition.eta.stops.find((s) => s.executionStopUid === stop.executionStopUid);
    if (eta?.isOverdue && stop.plannedAt) {
      items.push({
        id: `overdue-${stop.executionStopUid}`,
        priority: stop.type === 'unloading' ? 'critical' : 'high',
        icon: '⚠',
        title: `${stop.label} просрочена`,
        detail: `Опоздание: ${formatOverdueDuration(stop.plannedAt)}`,
        relatedStopUid: stop.executionStopUid,
        relatedIncidentId: null
      });
    } else if (eta?.deadlineMiss && stop.plannedAt) {
      items.push({
        id: `risk-${stop.executionStopUid}`,
        priority: 'high',
        icon: '⏱',
        title: `${stop.label} — риск опоздания`,
        detail: `План: ${new Date(stop.plannedAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`,
        relatedStopUid: stop.executionStopUid,
        relatedIncidentId: null
      });
    }
  }

  if (dashboard.financeReadiness.blocked) {
    items.push({
      id: 'finance-blocked',
      priority: 'high',
      icon: '💳',
      title: 'Оплата заблокирована',
      detail: dashboard.financeReadiness.blockedReasons.join(' · ') || dashboard.financeReadiness.headline,
      relatedStopUid: null,
      relatedIncidentId: null
    });
  }

  if ((dashboard.progress?.partialCargoCount ?? 0) > 0) {
    items.push({
      id: 'partial-delivery',
      priority: 'medium',
      icon: '📦',
      title: 'Частичная доставка',
      detail: `${dashboard.progress?.partialCargoCount} позиций`,
      relatedStopUid: null,
      relatedIncidentId: null
    });
  }

  if (openDocumentTasks > 0) {
    items.push({
      id: 'documents-pending',
      priority: 'medium',
      icon: '📄',
      title: 'Ожидаются документы',
      detail: `Открыто задач: ${openDocumentTasks}`,
      relatedStopUid: null,
      relatedIncidentId: null
    });
  }

  const downtime = dashboard.incidents.items.find(
    (i) => i.isActive && i.type === 'downtime'
  );
  if (downtime) {
    items.push({
      id: `downtime-${downtime.id}`,
      priority: 'high',
      icon: '⏸',
      title: 'Не закрыт простой',
      detail: downtime.comment,
      relatedStopUid: downtime.relatedExecutionStopUid,
      relatedIncidentId: downtime.id
    });
  }

  const order: ActionPriority[] = ['critical', 'high', 'medium', 'low'];
  return items.sort(
    (a, b) => order.indexOf(a.priority) - order.indexOf(b.priority)
  );
}

function pickPrimaryAction(
  dashboard: ExecutionDashboardProjection,
  actions: OperationalActionDescriptor[],
  composition: WorkspaceComposition
): DispatchPrimaryAction {
  const blocking = dashboard.incidents.items.find(
    (i) => i.isActive && (i.severity === 'high' || dashboard.incidents.hasActiveBlocking)
  );

  if (blocking) {
    return {
      label: 'Разрешить проблему',
      priority: 'critical',
      descriptor: actions.find((a) => a.opensIncident) ?? null,
      incidentId: blocking.id,
      opensCompletion: false
    };
  }

  if (dashboard.routeStatus.hasExecutionFailure) {
    return {
      label: 'Разрешить проблему',
      priority: 'critical',
      descriptor: actions.find((a) => a.opensIncident) ?? null,
      incidentId: null,
      opensCompletion: false
    };
  }

  const candidates = actions
    .filter((a) => !a.disabled && !a.opensIncident)
    .sort((a, b) => (ACTION_RANK[a.key] ?? 50) - (ACTION_RANK[b.key] ?? 50));

  const overdueUnload = dashboard.routeFlow.stops.find(
    (s) =>
      s.type === 'unloading' &&
      !s.isCompleted &&
      composition.attention.overdueStopUids.includes(s.executionStopUid)
  );

  if (overdueUnload) {
    const unloadAction = candidates.find(
      (a) =>
        a.key === 'unloaded' ||
        a.key === 'start_unloading' ||
        a.key === 'complete_unloading'
    );
    if (unloadAction) {
      return {
        label: 'Выгрузить',
        priority: 'critical',
        descriptor: unloadAction,
        incidentId: null,
        opensCompletion: false
      };
    }
  }

  const primary = candidates[0];
  if (primary) {
    let priority: ActionPriority = 'medium';
    if (primary.key === 'loaded' || primary.key === 'unloaded' || primary.key === 'start_loading') {
      priority = 'high';
    }
    if (primary.key === 'complete_route') priority = 'low';

    return {
      label: displayLabel(primary),
      priority,
      descriptor: primary,
      incidentId: null,
      opensCompletion: primary.key === 'complete_route'
    };
  }

  const complete = actions.find((a) => a.key === 'complete_route' && !a.disabled);
  if (complete) {
    return {
      label: 'Завершить маршрут',
      priority: 'low',
      descriptor: complete,
      incidentId: null,
      opensCompletion: true
    };
  }

  const report = actions.find((a) => a.opensIncident);
  return {
    label: 'Сообщить о проблеме',
    priority: 'medium',
    descriptor: report ?? null,
    incidentId: null,
    opensCompletion: false
  };
}

function deriveNextAction(
  primary: DispatchPrimaryAction,
  workflow: WorkflowOrchestration,
  dashboard: ExecutionDashboardProjection
): { label: string; detail: string | null } {
  if (primary.incidentId != null) {
    const inc = dashboard.incidents.items.find((i) => i.id === primary.incidentId);
    return {
      label: 'Закрыть инцидент или снять блокировку',
      detail: inc?.typeLabel ?? null
    };
  }

  const map: Record<string, string> = {
    loaded: 'Начать погрузку',
    start_loading: 'Начать погрузку',
    complete_loading: 'Завершить погрузку',
    in_transit: 'Отправить маршрут в путь',
    unloaded: 'Подтвердить выгрузку',
    start_unloading: 'Начать выгрузку',
    complete_unloading: 'Завершить выгрузку',
    partial_unloaded: 'Зафиксировать частичную выгрузку',
    complete_route: 'Завершить маршрут'
  };

  const key = primary.descriptor?.key ?? '';
  return {
    label: map[key] ?? primary.label,
    detail: workflow.subline
  };
}

export function buildDispatchOrchestration(
  dashboard: ExecutionDashboardProjection,
  options?: { openDocumentTasks?: number; composition?: WorkspaceComposition }
): DispatchOrchestration {
  const composition = options?.composition ?? buildWorkspaceComposition(dashboard);
  const openDocumentTasks = options?.openDocumentTasks ?? 0;
  const workflow = pickWorkflow(dashboard);
  const primary = pickPrimaryAction(dashboard, dashboard.actions, composition);
  const next = deriveNextAction(primary, workflow, dashboard);

  const primaryKey = primary.descriptor ? actionKey(primary.descriptor) : null;
  const secondaryActions = dashboard.actions.filter((a) => {
    if (primaryKey && actionKey(a) === primaryKey) return false;
    return true;
  });

  const attentionItems = buildAttentionItems(dashboard, composition, openDocumentTasks);

  const priorityOrder: ActionPriority[] = ['critical', 'high', 'medium', 'low'];
  const overallPriority =
    priorityOrder.find((p) => p === primary.priority) ??
    attentionItems[0]?.priority ??
    'low';

  return {
    priority: overallPriority,
    nextActionLabel: next.label,
    nextActionDetail: next.detail,
    primary,
    secondaryActions,
    workflow,
    attentionItems,
    composition
  };
}
